import { FormattedMessage, FormattedNumber } from 'react-intl'

import { CurrencyStringCents } from '@b-stock/fe-core/bstock-react'
import {
  type EuRunningTaxEstimateDto,
  EuRunningTaxEstimateDtoTreatmentEnum,
} from '@b-stock/listing-api-client'

import { Card, Row, Label, Amount, Legend } from '../taxUi'

// Listing-detail EU VAT running estimate (FR-007). Renders one of
// three states from the server DTO:
//
//   - `estimateUnavailable: true` → "estimate unavailable" line
//     (FR-020). Never render €0 for this — a zero says "no VAT is
//     due" on a supply that IS taxable.
//   - `treatment: REVERSE_CHARGE` → the amount (a real 0 per the DTO
//     contract) rendered alongside the legend (FR-021). Omitting the
//     row is what FR-021 forbids.
//   - `treatment: STANDARD` → the estimated VAT amount and rate.
//
// The rate is passed as a 5dp decimal string so callers can recompute
// `amount × rate` locally as the bid ticks (FR-007) — this component
// just renders whatever `estimatedVatAmount` the container decided on.

type RunningVatEstimateProps = {
  data: EuRunningTaxEstimateDto
  currencyCode: string
  /**
   * Optional client-recomputed VAT (cents). When provided, overrides
   * the DTO's `estimatedVatAmount` — the container passes this after
   * folding a fresh bid amount through the cached `rate` (FR-007). On
   * REVERSE_CHARGE the container should still pass 0 here; the legend
   * carries the meaning (FR-021).
   */
  estimatedVatAmountCentsOverride?: number
}

const RunningVatEstimate = ({
  data,
  currencyCode,
  estimatedVatAmountCentsOverride,
}: RunningVatEstimateProps) => {
  if (data.estimateUnavailable) {
    return (
      <Card data-testid="running-vat-estimate-unavailable">
        <Label>
          <FormattedMessage id="RunningVatEstimate.unavailable" />
        </Label>
      </Card>
    )
  }

  const isReverseCharge =
    data.treatment === EuRunningTaxEstimateDtoTreatmentEnum.ReverseCharge

  // Force 0 on REVERSE_CHARGE — a future caller that folds
  // `bid × rate` locally without special-casing the treatment would
  // otherwise pass a non-zero override, breaking FR-021.
  const amountCents = isReverseCharge
    ? 0
    : (estimatedVatAmountCentsOverride ?? data.estimatedVatAmount ?? 0)

  // 5dp string → number; `|| 0` guards against contract drift so the
  // rate renders 0% rather than NaN% if `rate` is unexpectedly absent.
  const rate = Number.parseFloat(data.rate ?? '0') || 0

  return (
    <Card data-testid="running-vat-estimate">
      <Row>
        <Label>
          <FormattedMessage
            id="RunningVatEstimate.label"
            values={{
              rate: (
                <FormattedNumber
                  value={rate}
                  style="percent"
                  // 3 decimals — matches MaxTaxExposureDisplay's precision
                  // so a 21% rate never silently rounds and a 2.1%-style
                  // reduced rate still surfaces exactly.
                  maximumFractionDigits={3}
                />
              ),
            }}
          />
        </Label>
        <Amount data-testid="running-vat-estimate-amount">
          <CurrencyStringCents value={amountCents} currency={currencyCode} />
        </Amount>
      </Row>
      {isReverseCharge && data.treatmentLegend && (
        <Legend data-testid="running-vat-estimate-legend">
          {data.treatmentLegend}
        </Legend>
      )}
    </Card>
  )
}

export default RunningVatEstimate
